import { useState, useRef, useMemo, useCallback } from "react";

const DAY_MS = 24 * 60 * 60 * 1000;
const HOUR_TICKS = [0, 3, 6, 9, 12, 15, 18, 21, 24];
const META = { unit: "steps", color: "#22c55e" };

function formatClock(ms) {
  if (!ms) return "--";
  return new Date(ms).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true });
}

function getStatus(cumulativeValue) {
  if (cumulativeValue == null || Number.isNaN(cumulativeValue)) return { label: "--", tone: "neutral" };
  return cumulativeValue >= 5000 ? { label: "On-track", tone: "good" } : { label: "Behind", tone: "warn" };
}

const TONE_STYLES = {
  good:    { color: "#16a34a", bg: "#dcfce7" },
  warn:    { color: "#d97706", bg: "#fef3c7" },
  bad:     { color: "#dc2626", bg: "#fee2e2" },
  neutral: { color: "#6b7280", bg: "#f1f5f9" },
};

function StatusChip({ tone, label }) {
  const s = TONE_STYLES[tone] || TONE_STYLES.neutral;
  return <span className="status-chip" style={{ color: s.color, background: s.bg }}>{label}</span>;
}

// Steps is cumulative through the day + rendered as bars, not a line.
export default function StepsGraph({ bucketData, selectedDate, dateControl }) {
  const wrapRef = useRef(null);
  const [hoverIdx, setHoverIdx] = useState(null);

  const dayStart = useMemo(() => {
    const d = new Date(selectedDate);
    d.setHours(0, 0, 0, 0);
    return d.getTime();
  }, [selectedDate]);

  const view = useMemo(() => ({ start: dayStart, end: dayStart + DAY_MS }), [dayStart]);
  const viewRange = view.end - view.start;

  const points = useMemo(
    () => bucketData.filter(b => b.time >= view.start && b.time <= view.end),
    [bucketData, view]
  );

  const totalSteps = useMemo(
    () => points.reduce((sum, p) => sum + (p.value || 0), 0),
    [points]
  );

  const maxBucket = useMemo(
    () => Math.max(1, ...points.map(p => p.value || 0)),
    [points]
  );

  const W = 1000, H = 220, PAD_L = 34, PAD_R = 10, PAD_T = 10, PAD_B = 24;
  const plotW = W - PAD_L - PAD_R;
  const plotH = H - PAD_T - PAD_B;

  const xForTime = useCallback(t => PAD_L + ((t - view.start) / viewRange) * plotW, [view, viewRange, plotW]);

  const bars = useMemo(() => {
    const barW = Math.max(1, plotW / points.length - 1);
    return points.map((p, i) => {
      const value = p.value || 0;
      const h = (value / maxBucket) * plotH;
      return {
        x: xForTime(p.time),
        y: PAD_T + plotH - h,
        w: barW,
        h,
        time: p.time,
        value,
      };
    });
  }, [points, maxBucket, plotH, xForTime, plotW]);

  const xTicks = useMemo(() => {
    return HOUR_TICKS.map(h => dayStart + h * 60 * 60 * 1000).filter(t => t >= view.start && t <= view.end);
  }, [dayStart, view]);

  const hovered = hoverIdx != null ? bars[hoverIdx] : null;

  const cumulativeAtHover = useMemo(() => {
    if (hoverIdx == null) return null;
    let sum = 0;
    for (let i = 0; i <= hoverIdx; i++) sum += bars[i].value;
    return sum;
  }, [hoverIdx, bars]);

  const hoveredStatus = cumulativeAtHover != null ? getStatus(cumulativeAtHover) : null;

  const handleMove = useCallback(e => {
    if (!bars.length) return;
    const rect = wrapRef.current.getBoundingClientRect();
    const relX = ((e.clientX - rect.left) / rect.width) * W;
    let nearest = 0, best = Infinity;
    bars.forEach((b, i) => { const d = Math.abs(b.x - relX); if (d < best) { best = d; nearest = i; } });
    setHoverIdx(nearest);
  }, [bars]);

  const handleLeave = useCallback(() => setHoverIdx(null), []);

  const overallStatus = getStatus(totalSteps);

  return (
    <div className="allgraph-container">
      <div className="graph-date-bar">{dateControl}</div>

      <div className="vital-chart-header">
        <div className="vital-chart-latest">
          <div className="vital-chart-latest-time">Total for the day</div>
          <div className="vital-chart-latest-value">
            {totalSteps.toLocaleString("en-IN")}
            <span className="vital-chart-latest-unit"> {META.unit}</span>
          </div>
          <StatusChip tone={overallStatus.tone} label={overallStatus.label} />
        </div>
      </div>

      <div className="allgraph-chart vital-chart-svg-wrap" ref={wrapRef} onMouseMove={handleMove} onMouseLeave={handleLeave} style={{ height: 260 }}>
        <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: "100%", height: "100%" }}>
          {xTicks.map(t => (
            <text key={t} x={xForTime(t)} y={H - 6} fontSize="9" fill="#9ca3af" textAnchor="middle">
              {new Date(t).toLocaleTimeString("en-IN", { hour: "numeric", hour12: true }).replace(" ", "")}
            </text>
          ))}

          {bars.map((b, i) => (
            <rect
              key={i}
              x={b.x} y={b.y} width={b.w} height={b.h}
              fill={META.color}
              opacity={hoverIdx === i ? 1 : 0.6}
            />
          ))}

          {hovered && (
            <line x1={hovered.x} x2={hovered.x} y1={PAD_T} y2={PAD_T + plotH} stroke="#94a3b8" strokeWidth={1} />
          )}
        </svg>

        {hovered && (
          <div className="vital-tooltip" style={{ left: `${(hovered.x / W) * 100}%`, top: `${(hovered.y / H) * 100}%`, borderColor: META.color }}>
            <div>{formatClock(hovered.time)}</div>
            <div style={{ color: META.color }}>Steps: {hovered.value.toLocaleString("en-IN")}</div>
            <div>Cumulative: {cumulativeAtHover?.toLocaleString("en-IN")}</div>
            <div>{hoveredStatus.label}</div>
          </div>
        )}
      </div>
    </div>
  );
}